import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Delete, EyeVisible, Upload } from '@factorialco/f0-react/icons/app'
import { ItAssetDocumentsDocument, ItAssetDocumentsQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import DocumentModal from 'modules/documents/components/DocumentModal'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { DeleteItAssetDocumentModal } from 'modules/it_management/components/DeleteItAssetDocument'
import UploadAssetDocumentModal from 'modules/it_management/components/UploadAssetDocumentModal'

type DocumentNode = NonNullable<
  NonNullable<
    ItAssetDocumentsQuery['itManagement']['itAssetDocumentsConnection']
  >['edges'][number]['node']
>

const AssetDocumentsPage = () => {
  const { assetId } = useParams<{ assetId: string }>()
  const { company } = useCurrentSession()
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<string | null>(null)
  const [allDocuments, setAllDocuments] = React.useState<DocumentNode[]>([])
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [documentToDelete, setDocumentToDelete] = React.useState<DocumentNode | null>(null)

  const source = useGraphqlDataCollectionSource({
    document: ItAssetDocumentsDocument,
    mapVariables: () => ({ itAssetId: assetId || '' }),
    mapData: (data) => {
      const docs =
        data?.itManagement?.itAssetDocumentsConnection?.edges?.map((edge) => edge.node) || []
      setAllDocuments(docs)
      return docs
    },
    mapPagination: (data) => ({
      total: data?.itManagement?.itAssetDocumentsConnection?.edges?.length ?? 0,
    }),
    primaryActions: () => ({
      label: i18n.t('it_management.asset_details.documents.upload_document'),
      icon: Upload,
      onClick: () => setShowUploadModal(true),
      variant: 'outline',
    }),
    itemActions: (doc: DocumentNode) => {
      const actions = []

      if (doc.documentId) {
        actions.push({
          label: i18n.t('it_management.asset_details.documents.view_document'),
          icon: EyeVisible,
          onClick: () => {
            setSelectedDocumentId(String(doc.documentId))
          },
        })
      }

      if (doc.documentId && doc.documentType === 'manually') {
        actions.push({
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => {
            setDocumentToDelete(doc)
          },
          critical: true,
        })
      }

      return actions
    },
  })

  const documents: Document[] = allDocuments
    .filter((doc) => doc !== null && doc.documentId !== null)
    .map((doc) => {
      const docId = doc.documentId || 0

      const documentObj = {
        id: docId,
        filename: doc.documentFilename || i18n.t('it_management.asset_details.documents.draft'),
        extension: doc.documentExtension || '',
        contentType: doc.documentContentType || '',
        author: doc.employee
          ? {
              id: doc.employee.id,
              fullName: `${doc.employee.firstName} ${doc.employee.lastName}`,
            }
          : undefined,
        company: {
          id: company.id,
        },
        space: doc.documentSpace || 'company_public',
        arCreatedAt: new Date(doc.createdAt).getTime() / 1000,
        type: doc.documentType,
        public: false,
        permissionUpdate: true,
        file: buildUrl(doc.documentFileUrl || ''),
        fileUrl: `/documents/${docId}`,
        createdAt: doc.createdAt,
        updatedAt: doc.createdAt,
      }

      return documentObj
    })

  if (!assetId) {
    return (
      <Box padding="s24">
        <OneEmptyState
          title={i18n.t('it_management.common.error_occurred')}
          description="Asset ID is required"
          variant="critical"
        />
      </Box>
    )
  }

  return (
    <Box padding="s24" flexDirection="column" gap="s24">
      {selectedDocumentId && (
        <DocumentModal
          documentId={selectedDocumentId}
          documents={documents}
          onClose={() => setSelectedDocumentId(null)}
        />
      )}

      {showUploadModal && assetId && (
        <UploadAssetDocumentModal assetId={assetId} onClose={() => setShowUploadModal(false)} />
      )}

      {documentToDelete && assetId && (
        <DeleteItAssetDocumentModal
          document={documentToDelete}
          assetId={assetId}
          onClose={() => setDocumentToDelete(null)}
        />
      )}

      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            emoji: '📄',
            title: i18n.t('it_management.asset_details.documents.no_documents_title'),
            description: i18n.t('it_management.asset_details.documents.no_documents_description'),
            actions: [
              {
                label: i18n.t('it_management.asset_details.documents.upload_document'),
                icon: Upload,
                onClick: () => setShowUploadModal(true),
                variant: 'outline',
              },
            ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('it_management.asset_details.documents.file_name'),
                  render: (doc: DocumentNode) => ({
                    type: 'text',
                    value:
                      doc.documentFilename || i18n.t('it_management.asset_details.documents.draft'),
                  }),
                },
                {
                  label: i18n.t('it_management.asset_details.documents.employee'),
                  render: (doc: DocumentNode) => {
                    if (!doc.employee) {
                      return {
                        type: 'text',
                        value: '-',
                      }
                    }

                    return {
                      type: 'person',
                      value: {
                        firstName: doc.employee.firstName,
                        lastName: doc.employee.lastName,
                        src: buildUrl(doc.employee.avatar?.url || ''),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
    </Box>
  )
}

export default AssetDocumentsPage
